import { useState } from 'react';
import { toast } from 'react-toastify';
import BillingContactExternalForm from 'components/billingContacts/BillingContactExternalForm';
import {
  contactFormFrom,
  emptyContactForm,
  toContactPayload,
  validateContactForm,
} from 'components/billingContacts/billingContactForm';
import Button from 'components/button';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import { useNotifyRecordChanged } from 'hooks/useRecordChanged';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import useText from 'hooks/useText';
import crmApi from 'server/crm/crmApi';
import { useInviteOnJobbiz } from './billingContactModal';
import { companyContactErrorText } from './companyContactErrors';

export const ADD_COMPANY_CONTACT_MODAL = 'add_company_contact';

/**
 * « Ajouter un contact » of a company (JBWEB-1424): a contact without an
 * account, the billing contact's form without its boxes but with a required
 * position (BillingExternalView). « Inviter sur Jobbiz » turns it into an
 * invitation (add_company_member, filled with what was typed), at a prospect
 * as at a client; an edited contact goes with its id, so that the account
 * created is linked to it. `modalData`: { idCompany, contact? } — with
 * `contact`, the contact is edited, or deleted.
 */
const ModalAddCompanyContact = () => {
  const { toggle } = useModal();
  const { modalData } = UseContext();
  const { text, language } = useText('companyContact');
  const { call } = useCrmContext();
  const notify = useNotifyRecordChanged();
  const inviteOnJobbiz = useInviteOnJobbiz();
  // Read once: « Inviter sur Jobbiz » replaces « modalData » for the next modal.
  const [{ idCompany, contact }] = useState(() => ({
    idCompany: modalData?.idCompany,
    contact: modalData?.contact ?? null,
  }));
  const isEdit = Boolean(contact);
  const [form, setForm] = useState(() =>
    isEdit ? contactFormFrom(contact) : emptyContactForm(),
  );
  const [errors, setErrors] = useState({});
  const [isSending, setIsSending] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const changeField = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => ({ ...previous, [field]: undefined }));
  };

  const run = (request, message) => {
    setIsSending(true);
    request.then(
      () => {
        setIsSending(false);
        toast.success(message);
        notify();
        toggle();
      },
      (error) => {
        setIsSending(false);
        if (error?.name === 'CanceledError') return;
        toast.error(companyContactErrorText(language, error));
      },
    );
  };

  const save = () => {
    // Without an address, the contact is sent with email: '' (the api checks
    // an address only when there is one).
    const found = validateContactForm(form, { isEmailOptional: true });
    if (Object.keys(found).length) {
      setErrors(found);
      return;
    }
    run(
      call(crmApi.saveCompanyContact, {
        companyId: idCompany,
        contact: {
          ...(isEdit ? { id: contact.id } : {}),
          ...toContactPayload(form),
        },
      }),
      text(isEdit ? 'saved' : 'added'),
    );
  };

  const errorTexts = Object.fromEntries(
    Object.entries(errors)
      .filter(([, key]) => key)
      .map(([field, key]) => [
        field,
        language.getValue(`companyBillingContacts.errors.${key}`, ''),
      ]),
  );
  const name =
    [contact?.firstname, contact?.lastname].filter(Boolean).join(' ') ||
    contact?.email;

  if (isConfirmingDelete) {
    return (
      <>
        <Modalheader hide={toggle} hideShow={!isSending}>
          {text('deleteTitle', { name })}
        </Modalheader>
        <ModalBody>
          <p className="text-sm text-gray-700">{text('deleteConfirmation')}</p>
        </ModalBody>
        <ModalFooter>
          <Button
            size="LG"
            color={isSending ? 'DISABLED' : 'SECONDARY'}
            disabled={isSending}
            onClick={() => setIsConfirmingDelete(false)}
          >
            {text('cancel')}
          </Button>
          <Button
            size="LG"
            color={isSending ? 'DISABLED' : 'PRIMARY'}
            disabled={isSending}
            onClick={() =>
              run(
                call(crmApi.deleteCompanyContact, {
                  companyId: idCompany,
                  contactId: contact.id,
                }),
                text('deleted'),
              )
            }
          >
            {text(isSending ? 'deleting' : 'confirmDelete')}
          </Button>
        </ModalFooter>
      </>
    );
  }

  return (
    <>
      <Modalheader
        hide={toggle}
        hideShow={!isSending}
        description={text('description')}
      >
        {text(isEdit ? 'editTitle' : 'addTitle')}
      </Modalheader>
      <ModalBody>
        <div className="flex flex-col gap-4">
          <BillingContactExternalForm
            form={form}
            errors={errorTexts}
            onChange={changeField}
            isDisabled={isSending}
            withNotifications={false}
            withPosition
            isEmailOptional
          />
          {isEdit && (
            <button
              type="button"
              disabled={isSending}
              onClick={() => setIsConfirmingDelete(true)}
              className="self-start text-sm text-red-600 underline underline-offset-4 disabled:text-gray-400"
            >
              {text('delete')}
            </button>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          disabled={isSending}
          onClick={() =>
            inviteOnJobbiz(
              idCompany,
              form,
              isEdit ? { companyContactId: contact.id } : {},
            )
          }
          className="mr-auto text-sm text-gray-900 underline decoration-dotted underline-offset-4 disabled:text-gray-400"
        >
          {text('invite')}
        </button>
        <Button
          size="LG"
          color={isSending ? 'DISABLED' : 'SECONDARY'}
          disabled={isSending}
          onClick={toggle}
        >
          {text('cancel')}
        </Button>
        <Button
          size="LG"
          color={isSending ? 'DISABLED' : 'PRIMARY'}
          disabled={isSending}
          onClick={save}
        >
          {text(isSending ? 'saving' : isEdit ? 'save' : 'add')}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalAddCompanyContact;
